import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { sportsMenuFullQuery } from '@/api/queries';
import { translateSportsLabel } from '@/i18n/sports-label';
import { resolveMenuPath } from '@/shell/sports-menu-utils';
import { EmptyState } from '@/ui';
import { EventsFeedSkeleton } from './-components/EventsFeed';
import { OutrightsMarketsPanel } from './-components/OutrightsMarketsPanel';
import { TournamentOfferTabs } from './-components/TournamentOfferTabs';
import { useTournamentSeasons } from './-components/useTournamentSeasons';

export const Route = createFileRoute('/sports/$sport/$category/$tournament/outrights')({
  component: TournamentOutrightsPage,
});

function TournamentOutrightsPage() {
  const { sport, category, tournament } = Route.useParams();
  const { t, i18n } = useTranslation();
  const menuQuery = useQuery(sportsMenuFullQuery());

  const resolved = useMemo(
    () => resolveMenuPath(menuQuery.data, sport, category, tournament),
    [menuQuery.data, sport, category, tournament],
  );

  const seasonFilters = useMemo(
    () =>
      resolved
        ? {
            Sport: resolved.sport.Name,
            Category: resolved.category.Name,
            Tournament: resolved.tournament.Name,
          }
        : null,
    [resolved],
  );

  const seasons = useTournamentSeasons(seasonFilters);

  if (menuQuery.isPending || seasons.isLoading) {
    return <EventsFeedSkeleton />;
  }

  if (!resolved || !seasonFilters) {
    return (
      <EmptyState
        icon="topleagues"
        title={t('TournamentNotFound', { defaultValue: 'Tournament not found' })}
        description={t('TournamentNotInMenu', {
          defaultValue: 'This tournament is not in the current sports menu.',
        })}
      />
    );
  }

  const sportLabel = translateSportsLabel(i18n, t, resolved.sport.Id, resolved.sport.Name);
  const categoryLabel = translateSportsLabel(i18n, t, resolved.category.Id, resolved.category.Name);
  const tournamentLabel = translateSportsLabel(
    i18n,
    t,
    resolved.tournament.Id,
    resolved.tournament.Name,
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-heading-m text-text-primary">{tournamentLabel}</h1>
          <p className="text-body-s text-text-secondary">{`${categoryLabel} · ${sportLabel}`}</p>
        </div>
        {seasons.showTabs ? (
          <TournamentOfferTabs
            value="outrights"
            sport={sport}
            category={category}
            tournament={tournament}
          />
        ) : null}
      </header>

      {seasons.isError ? (
        <EmptyState
          icon="warning"
          title={t('CouldNotLoadOutrights', { defaultValue: 'Could not load outrights' })}
          description={t('TryAgainInAMoment', { defaultValue: 'Try again in a moment.' })}
        />
      ) : seasons.season ? (
        <OutrightsMarketsPanel season={seasons.season} />
      ) : (
        <EmptyState
          icon="topleagues"
          title={t('Outrights', { defaultValue: 'Outrights' })}
          description={t('NoOutrightsForTournament', {
            defaultValue: 'No outrights available for this tournament right now.',
          })}
        />
      )}
    </div>
  );
}
